import React, { useCallback, useEffect, useState } from 'react';
import { useFocusEffect, useNavigation, useRoute } from '@react-navigation/native';
import { StyleSheet, View, SafeAreaView } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import BaseText from '../components/BaseText';
import FloatButton from '../components/FloatButton';
import InputDialog from '../components/InputDialog';
import InputForm from '../components/InputForm';
import MultipleSelect from '../components/SegmentedControl/MultipleSelect';
import { Modals, Screens } from '../constants/ScreenNames';
import { selectExpenseList } from '../store/selectors/expense.selector';
import { Color, CommonProperties, CommonStyles } from '../styles';
import { leftMenuHeaderOption, rightExpenseHeaderOption } from '../utils/defaultNavOptions';
import useTextExpenseFilters from '../utils/useTextExpenseFilters';
import {
  deleteExpenseReport,
  getExpenseAttendees,
  moveExpenseNew,
  setChargesInUseForExpense,
  setCurrentReportIdForUpdate,
  setExpenseResults,
  setReceiptsOfCurrentUser,
  updateReport,
} from '../store/actions/expense';
import MoveExpenseModal from '../components/expense/MoveExpenseModal';
import SubmitExpenseModal from '../components/expense/SubmitExpenseModal';
import ExpenseReportCard, { RowBack } from '../components/expense/ExpenseReportCard';
import { useTranslation } from 'react-i18next';
import { i18nNS, useComponentTKey } from '../i18n';
import { SwipeListView } from 'react-native-swipe-list-view';
import { scale } from '../utils/scale.utils';
import BottomSheet from 'react-native-simple-bottom-sheet';
import { TouchableWithoutFeedback } from 'react-native-gesture-handler';
import { RadioButton } from '../components/RadioGroup';
import RowView from '../components/RowView';
import { isNullOrEmpty } from '../utils/common.utils';
import { EXPENSE_SUB_TAB_OPTIONS, expenseActions, REPORT_STATUS } from '../constants/Enums';
import { getReceiptsOfUser } from '../services/api/expense.service';
import moment from 'moment';

const { PENDING, SUBMITTED } = REPORT_STATUS;
const { MANUAL } = EXPENSE_SUB_TAB_OPTIONS;

const ExpenseScreen = (props) => {
  const { t } = useTranslation(i18nNS.tripExpense);
  const { tKey } = useComponentTKey('ExpenseScreen');
  const navigation = useNavigation();
  const route = useRoute();
  const filters = useTextExpenseFilters();
  const dispatch = useDispatch();
  const expenseList = useSelector(selectExpenseList);
  const [filterStatus, setFilterStatus] = useState(PENDING);
  const [searchValue, setSearchValue] = useState('');
  const [moveModal, setMoveModal] = useState(false);
  const [submitModal, setSubmitModal] = useState(false);
  const [itemSelected, setItemSelected] = useState({});
  const [subItemSelected, setSubItemSelected] = useState(null);
  const [editExpenseName, setEditExpenseName] = useState(false);
  const [business, setBusiness] = useState('');
  const [fraud, setFraud] = useState('');
  const [personal, setPersonal] = useState('');
  const [visibleBottomSheet, setVisibleBottomSheet] = useState(false);

  const expenseListFiltered = useCallback(() => {
    if (isNullOrEmpty(expenseList)) {
      return [];
    }
    if (isNullOrEmpty(expenseList)) {
      return [];
    }
    return expenseList.filter((item) =>
      filterStatus ? item.Status === filterStatus || item.ReportType === filterStatus : true
    );
  }, [expenseList, filterStatus]);

const sortedList = useCallback(() => {
    const filteredList = expenseListFiltered();
    if (isNullOrEmpty(filteredList) || filteredList === false) {
        return [];
    }

    const sortedList = filteredList?.sort(
        (a, b) => new Date(b.CreatedDate) - new Date(a.CreatedDate)
    );
    if (isNullOrEmpty(searchValue)) {
        return sortedList;
    }

    const lowercasedSearch = searchValue.toLowerCase();

    return sortedList?.filter((item) => {
  const itemText = JSON.stringify(item).toLowerCase();
  const formattedDate = moment(item.StartDate).format('MMM D, YYYY').toLowerCase();
  return itemText.includes(lowercasedSearch) || formattedDate.includes(lowercasedSearch);
});
}, [expenseListFiltered, searchValue]);

  const expenseListFilteredByName = sortedList();

  useFocusEffect(
    useCallback(() => {
      const { resultingReport } = route.params || {}; // Check if an expense was created or edited
      if (!isNullOrEmpty(resultingReport)) {
        // Navigate automatically to the modal and pass the necessary props
        navigation.navigate(Modals.ExpenseReportCardModal, {
          item: resultingReport,
          onMoveItem: (item, expenseId) => onMoveReport(item, expenseId),
          onAddExpenseItem: () => onAddExpense(resultingReport),
          onsubmitExpense: (updated) => onSubmitReport(updated),
          onEditName: () => onEditReportName(resultingReport),
          onViewPDF,
        });
        navigation.setParams({ resultingReport: undefined });
      }
    }, [route.params])
  );

  useEffect(() => {
    navigation.setOptions({
      headerTitle: () => (
        <InputForm
          value={searchValue}
          placeholder={t(tKey('FindExpenseReport'))}
          style={[CommonStyles.mb0, CommonStyles.trim]}
          clearButtonMode="always"
          width={250}
          onChangeText={(text) => {
            setSearchValue(text);
          }}
        />
      ),
      headerTitleAlign: 'left',
      ...rightExpenseHeaderOption(() => {
        setVisibleBottomSheet(!visibleBottomSheet);
      }),
      ...leftMenuHeaderOption(props),
    });
  }, [props, setSearchValue, searchValue, t, tKey, visibleBottomSheet]);

  useEffect(() => {
    async function getRes() {
      const actionType = { ActionCode: expenseActions.GET_REPORTS_AND_EXPENSES };
      await dispatch(setExpenseResults(actionType));
      //   await dispatch(getExpenseAttendees());
    }
    getRes();
  }, []);

  useEffect(() => {
    async function fetchReceipts() {
      const userReceipts = await getReceiptsOfUser();
      dispatch(setReceiptsOfCurrentUser(userReceipts));
    }
    fetchReceipts();
  }, []);

  async function handleDeleteExpenseReport(item) {
    const params = {
      ActionCode: expenseActions.DELETE_EXPENSE,
      ReportId: item.ReportId,
    };
    dispatch(deleteExpenseReport(params));
    restoreChargesFromReport(item);
  }

  async function restoreChargesFromReport(item) {
    const creditCardList = [];
    item.Expenses?.forEach((expense) => {
      if (expense?.Charge && Object.keys(expense?.Charge).length > 0) {
        creditCardList.push({
          TransactionId: expense?.Charge?.TransactionId,
          FirstSixLastFourCardNumber: expense?.Charge?.FirstSixLastFourCardNumber,
        });
      }
    });

    if (creditCardList.length > 0) {
      dispatch(setChargesInUseForExpense(creditCardList));
    }
  }

  function handleRadioChange(label) {
    if (label === business || label === personal || label === fraud) {
      setBusiness('');
      setPersonal('');
      setFraud('');
      setFilterStatus('');
    } else {
      setFilterStatus(label);
      setBusiness(label === radioInputLabels.business ? radioInputLabels.business : '');
      setPersonal(label === radioInputLabels.personal ? radioInputLabels.personal : '');
      setFraud(label === radioInputLabels.fraud ? radioInputLabels.fraud : '');
    }
  }

  const onEditReportName = (item) => {
    setItemSelected(item);
    setEditExpenseName(true);
  };

  const onSubmitReport = (item) => {
    setItemSelected(item);
    dispatch(
      updateReport(expenseActions.UPDATE_REPORT, item.ReportId, 'Report', {
        Recipient: item.reportRecipient,
        Message: item.reportMessage,
      })
    );
    dispatch(updateReport(expenseActions.UPDATE_STATUS_REPORT, item.ReportId, 'Status', SUBMITTED));
    setSubmitModal(true);
    setFilterStatus(SUBMITTED);
    navigation.navigate(Screens.Expense);
  };
  const onMoveReport = (item, expenseId) => {
    setItemSelected(item);
    setSubItemSelected(expenseId);
    setMoveModal(true);
  };

  const onAddExpense = (item) => {
    dispatch(setCurrentReportIdForUpdate(item.ReportId));
    navigation.navigate(Modals.TripExpenseAddModal, {
      selectedType: MANUAL,
      fromExistingReport: true,
    });
  };

  const onViewPDF = () => {};

  return (
    <>
      <SafeAreaView style={styles.container}>
        <View style={CommonStyles.flex1}>
          <MultipleSelect
            styleProps={multiSelectOptions}
            items={filters}
            onChange={(value) => setFilterStatus(value)}
            selected={filterStatus}
          />
          <MoveExpenseModal
            visible={moveModal}
            originExpenseId={itemSelected?.ReportId}
            onSubmit={(id) => {
              dispatch(
                moveExpenseNew(
                  expenseActions.MOVE_EXPENSE,
                  itemSelected?.Expenses.filter((item) => item.ExpenseId === subItemSelected)[0]
                    .CategoryId,
                  id
                )
              );
              setMoveModal(false);
            }}
            onClose={() => setMoveModal(false)}
          />
          <SubmitExpenseModal
            visible={submitModal}
            onClose={() => setSubmitModal(false)}
            item={itemSelected}
          />
          <InputDialog
            label={t(tKey('ChangeExpenseName'))}
            defaultValue={itemSelected?.Name}
            visible={editExpenseName}
            onCancel={() => setEditExpenseName(false)}
            onSubmit={(text) => {
              dispatch(
                updateReport(
                  expenseActions.UPDATE_EXPENSE_REPORT_NAME,
                  itemSelected.ReportId,
                  'Name',
                  text
                )
              );
              setEditExpenseName(false);
            }}
            loading={false}
          />
          <SwipeListView
            key={JSON.stringify(expenseListFilteredByName)}
            disableRightSwipe
            style={CommonStyles.flex1}
            data={!isNullOrEmpty(expenseListFilteredByName) ? expenseListFilteredByName : []}
            showsVerticalScrollIndicator={false}
            keyExtractor={(item) => item.ReportId}
            renderItem={(data) => {
              const { item } = data;
              return (
                <ExpenseReportCard
                  props={props}
                  item={item}
                  onEditName={() => onEditReportName(item)}
                  onsubmitExpense={onSubmitReport}
                  onMoveItem={onMoveReport}
                  onViewPDF={onViewPDF}
                  onAddExpenseItem={() => onAddExpense(item)}
                />
              );
            }}
            renderHiddenItem={(val, rowMap) => {
              return val.item.Status === PENDING ? (
                <RowBack
                  style={styles.rowBack}
                  onDelete={() => {
                    if (rowMap[val.item.ReportId]) {
                      rowMap[val.item.ReportId].closeRow();
                    }
                    handleDeleteExpenseReport(val.item);
                  }}
                />
              ) : null;
            }}
            rightOpenValue={scale(-55)}
            previewRowKey={'0'}
            previewOpenValue={-40}
            previewOpenDelay={2000}
            ListEmptyComponent={() => (
              <BaseText style={styles.textInfo}>{t(tKey('NoReportsCreated'))}</BaseText>
            )}
          />

          {filterStatus !== SUBMITTED && (
            <FloatButton
              style={{ backgroundColor: Color.primary }}
              onPress={() =>
                navigation.navigate(Modals.TripExpenseAddModal, {
                  selectedType: MANUAL,
                })
              }
              iconName="clip"
              iconStyle={{ color: Color.accent }}
            />
          )}
        </View>
      </SafeAreaView>
      {visibleBottomSheet && (
        <View>
          <BottomSheet onClose={() => setVisibleBottomSheet(false)}>
            <View>
              <BaseText style={styles.bottomSheetBaseText}>Filter by charge</BaseText>
              <View style={styles.bottomSheetView}>
                <RadioInput
                  textStyle={styles.radioBtnTextStyle}
                  label="Business"
                  onChange={() => handleRadioChange(radioInputLabels.business)}
                  selected={business}
                />
                <RadioInput
                  textStyle={styles.radioBtnTextStyle}
                  label="Personal"
                  onChange={() => handleRadioChange(radioInputLabels.personal)}
                  selected={personal}
                />
                <RadioInput
                  textStyle={styles.radioBtnTextStyle}
                  label="Fraud"
                  onChange={() => handleRadioChange(radioInputLabels.fraud)}
                  selected={fraud}
                />
              </View>
            </View>
          </BottomSheet>
        </View>
      )}
    </>
  );
};

export default ExpenseScreen;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Color.accent,
  },
  textInfo: {
    textAlign: 'center',
    ...CommonProperties.h3,
    ...CommonProperties.mt3,
  },
  rowBack: {
    flex: 1,
    paddingVertical: 6,
    paddingHorizontal: 11,
    flexDirection: 'row',
    justifyContent: 'flex-end',
    height: 100,
  },
  option: {
    ...CommonProperties.py3,
    ...CommonProperties.px4,
    ...CommonProperties.borderRadius3,
    ...CommonProperties.mr3,
    ...CommonProperties.my0,
    borderColor: '#707070',
    borderWidth: 1,
  },
  optionActive: {
    backgroundColor: Color.tezNewBlue,
    borderColor: Color.tezNewBlue,
  },
  optionText: {
    color: 'black',
  },
  radioBtnStyle: {
    borderTopColor: Color.tezNewBlue,
    borderBottomColor: Color.tezNewBlue,
    borderLeftColor: Color.tezNewBlue,
    borderRightColor: Color.tezNewBlue,
  },
  bottomSheetBaseText: { fontSize: 20, marginLeft: 'auto', marginRight: 'auto' },
  bottomSheetView: { gap: 10, marginBottom: 10 },
  radioBtnTextStyle: { fontSize: 16 },
});

const RadioInput = (props) => {
  return (
    <TouchableWithoutFeedback onPress={props.onChange}>
      <RowView style={CommonStyles.centerItems}>
        <RadioButton
          style={styles.radioBtnStyle}
          innerSelectedColor={Color.tezNewBlue}
          selected={props.selected}
        />
        <BaseText style={props.textStyle}>{props.label}</BaseText>
      </RowView>
    </TouchableWithoutFeedback>
  );
};

const multiSelectOptions = {
  option: styles.option,
  optionActive: styles.optionActive,
  optionText: styles.optionText,
};

const radioInputLabels = {
  business: 'Business',
  personal: 'Personal',
  fraud: 'Fraud',
};
